import { getSupabase } from '@/lib/supabase';
import type { PlayerGroup } from '@/schemas';
import type { UserId } from '@/schemas/ids';
import type { RealtimeSubscription } from '../realtime';
import { decodeRowToPlayerGroup, type PlayerGroupRow } from '../sync/codec';

export type PlayerGroupChangeType = 'INSERT' | 'UPDATE' | 'DELETE';

export interface PlayerGroupChange {
  type: PlayerGroupChangeType;
  group: PlayerGroup | null;
}

export async function subscribeToPlayerGroups(
  userId: UserId,
  onChange: (change: PlayerGroupChange) => void,
): Promise<RealtimeSubscription> {
  const client = getSupabase();
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session?.user) {
    throw new Error('User must be authenticated to subscribe to player groups');
  }

  const channel = client.channel(`player-groups:${userId}`);
  const typedChannel = channel as unknown as {
    on: (
      event: 'postgres_changes',
      filter: {
        event: 'INSERT' | 'UPDATE' | 'DELETE';
        schema: string;
        table: string;
        filter: string;
      },
      callback: (payload: { new?: PlayerGroupRow; old?: PlayerGroupRow }) => void,
    ) => typeof channel;
  };

  typedChannel.on(
    'postgres_changes',
    {
      event: 'INSERT',
      schema: 'public',
      table: 'player_groups',
      filter: `user_id=eq.${userId}`,
    },
    (payload) => {
      try {
        const group = decodeRowToPlayerGroup(payload.new as PlayerGroupRow);
        onChange({ type: 'INSERT', group });
      } catch (error: unknown) {
        const err = error instanceof Error ? error : new Error(String(error));
        console.warn('Failed to decode realtime player group (INSERT)', err);
      }
    },
  );

  typedChannel.on(
    'postgres_changes',
    {
      event: 'UPDATE',
      schema: 'public',
      table: 'player_groups',
      filter: `user_id=eq.${userId}`,
    },
    (payload) => {
      try {
        const group = decodeRowToPlayerGroup(payload.new as PlayerGroupRow);
        onChange({ type: 'UPDATE', group });
      } catch (error: unknown) {
        const err = error instanceof Error ? error : new Error(String(error));
        console.warn('Failed to decode realtime player group (UPDATE)', err);
      }
    },
  );

  typedChannel.on(
    'postgres_changes',
    {
      event: 'DELETE',
      schema: 'public',
      table: 'player_groups',
      filter: `user_id=eq.${userId}`,
    },
    (payload) => {
      try {
        const group = decodeRowToPlayerGroup(payload.old as PlayerGroupRow);
        onChange({ type: 'DELETE', group });
      } catch (error: unknown) {
        const err = error instanceof Error ? error : new Error(String(error));
        console.warn('Failed to decode realtime player group (DELETE)', err);
      }
    },
  );

  channel.subscribe();

  return {
    unsubscribe: () => {
      void client.removeChannel(channel);
    },
  };
}
